import { useState, type MouseEvent as ReactMouseEvent } from 'react'
import { AlertCircle, ChevronDown, ChevronUp, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useProductTypeDemand } from '@/hooks/useProductTypeDemand'
import { isWarmingError } from '@/hooks/useDashboard'
import { useDashboardStore } from '@/store/dashboardStore'
import { ApiError } from '@/lib/api/rawFetch'
import type { ProductTypeDemandRow } from '@/lib/api/session11Types'

type SortKey = keyof ProductTypeDemandRow

// Default column widths (px) — also the colgroup baseline before any resize.
const PT_COL_WIDTHS: Record<string, number> = {
  product_type: 260,
  active_units_60d: 150,
  units_ly: 150,
  forward_window: 150,
}
const PT_MIN_WIDTH = 36

function PtResizeHandle({
  label,
  onResizeStart,
}: {
  label: string
  onResizeStart: (e: ReactMouseEvent) => void
}) {
  return (
    <div
      className="dash-th-resize"
      onMouseDown={onResizeStart}
      onClick={(e) => e.stopPropagation()}
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label} column`}
    />
  )
}

function downloadCsv(filename: string, header: string[], rows: (string | number)[][]) {
  const esc = (v: string | number) => {
    const s = String(v ?? '')
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const body = [header.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\n')
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function ProductTypeDemandTable() {
  const { data: rows, isLoading, isError, error, refetch } = useProductTypeDemand()
  const { ptColumnSizing, setPtColumnSizing } = useDashboardStore()
  const [sortKey, setSortKey] = useState<SortKey>('units_ly')
  const [sortDesc, setSortDesc] = useState(true)

  const widthOf = (key: string) => ptColumnSizing[key] ?? PT_COL_WIDTHS[key] ?? 120

  function startResize(key: string, e: ReactMouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startW = widthOf(key)
    const base = { ...ptColumnSizing }
    const onMove = (ev: MouseEvent) => {
      const next = Math.max(PT_MIN_WIDTH, startW + (ev.clientX - startX))
      setPtColumnSizing({ ...base, [key]: next })
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  if (isLoading) {
    return (
      <div className="p-4 space-y-1">
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton key={i} className="h-7 w-full" />
        ))}
      </div>
    )
  }

  if (isError && isWarmingError(error)) {
    return (
      <div className="p-8 flex items-center justify-center gap-2 text-xs text-[var(--mute)]" data-testid="pt-warming">
        <Loader2 className="h-4 w-4 animate-spin" />
        Building dashboard data… this takes up to 3 minutes after a restart.
      </div>
    )
  }

  if (isError) {
    const is503 = error instanceof ApiError && error.status === 503
    return (
      <div className="p-8 flex flex-col items-center gap-3 text-center">
        <AlertCircle className="h-8 w-8 text-[var(--status-rejected-fg)]" />
        <p className="text-sm text-[var(--ink-2)]">
          {is503 ? 'Dashboard cache is warming up — try again in a minute' : 'Failed to load product type demand'}
        </p>
        <Button size="sm" variant="outline" className="text-xs" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    )
  }

  if (!rows || rows.length === 0) {
    return <div className="p-8 text-center text-sm text-[var(--mute)]">No product type data.</div>
  }

  const sorted = [...rows].sort((a, b) => {
    const av = a[sortKey]
    const bv = b[sortKey]
    if (av == null && bv == null) return 0
    if (av == null) return 1
    if (bv == null) return -1
    if (typeof av === 'number' && typeof bv === 'number') return sortDesc ? bv - av : av - bv
    return sortDesc ? String(bv).localeCompare(String(av)) : String(av).localeCompare(String(bv))
  })

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDesc(!sortDesc)
    else {
      setSortKey(key)
      setSortDesc(true)
    }
  }

  const SortIcon = ({ k }: { k: SortKey }) =>
    sortKey === k ? (sortDesc ? <ChevronDown className="inline h-3 w-3" /> : <ChevronUp className="inline h-3 w-3" />) : null

  const handleExport = () => {
    downloadCsv(
      'product-type-demand.csv',
      ['Product Type', 'Active Units (60D)', 'Units Last Year', 'Forward Window'],
      sorted.map((r) => [r.product_type, r.active_units_60d, r.units_ly, r.forward_window])
    )
  }

  // delayDuration=150ms — match the Dashboard table so the two tabs feel
  // consistent. Faster than the browser-native title attribute.
  return (
    <TooltipProvider delayDuration={150}>
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-[var(--line-warm)]">
        <span className="text-xs text-[var(--mute)]">{rows.length} product types</span>
        <div className="flex-1" />
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleExport}>
          Export CSV
        </Button>
      </div>
      <div className="flex-1 overflow-auto">
        <table
          className="text-xs"
          style={{
            tableLayout: 'fixed',
            width:
              widthOf('product_type') +
              widthOf('active_units_60d') +
              widthOf('units_ly') +
              widthOf('forward_window'),
          }}
        >
          <colgroup>
            <col style={{ width: widthOf('product_type') }} />
            <col style={{ width: widthOf('active_units_60d') }} />
            <col style={{ width: widthOf('units_ly') }} />
            <col style={{ width: widthOf('forward_window') }} />
          </colgroup>
          <thead className="sticky top-0 bg-[var(--surface-header)] border-b border-[var(--line-warm)]">
            <tr className="text-left text-[var(--mute)]">
              <th
                className="tbl-th normal-case tracking-[0.02em] cursor-pointer relative overflow-hidden"
                onClick={() => toggleSort('product_type')}
              >
                Product Type <SortIcon k="product_type" />
                <PtResizeHandle label="Product Type" onResizeStart={(e) => startResize('product_type', e)} />
              </th>
              <Tooltip>
                <TooltipTrigger asChild>
                  <th
                    className="tbl-th normal-case tracking-[0.02em] text-right cursor-pointer relative overflow-hidden"
                    onClick={() => toggleSort('active_units_60d')}
                  >
                    Active Units (60D) <SortIcon k="active_units_60d" />
                    <PtResizeHandle label="Active Units (60D)" onResizeStart={(e) => startResize('active_units_60d', e)} />
                  </th>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-sm whitespace-pre-line text-left">
                  Total units sold in the last 60 days across all variants in this product type.{'\n'}
                  Active Units (60D) = Σ qty_sold_60d for variants where status = active
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <th
                    className="tbl-th normal-case tracking-[0.02em] text-right cursor-pointer relative overflow-hidden"
                    onClick={() => toggleSort('units_ly')}
                  >
                    Units Last Year <SortIcon k="units_ly" />
                    <PtResizeHandle label="Units Last Year" onResizeStart={(e) => startResize('units_ly', e)} />
                  </th>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-sm whitespace-pre-line text-left">
                  Total units sold in the same window one year ago — used for seasonal lookup.{'\n'}
                  Units Last Year = Σ qty_sold for variants in this product type, 365 days prior to today's 60D window
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <th
                    className="tbl-th normal-case tracking-[0.02em] text-right cursor-pointer relative overflow-hidden"
                    onClick={() => toggleSort('forward_window')}
                  >
                    Forward Window <SortIcon k="forward_window" />
                    <PtResizeHandle label="Forward Window" onResizeStart={(e) => startResize('forward_window', e)} />
                  </th>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-sm whitespace-pre-line text-left">
                  Projected units for the next 60 days based on velocity × seasonal lift.{'\n'}
                  Forward Window = round( Active Units (60D) × (Units Last Year ÷ Active Units LY same window) )
                </TooltipContent>
              </Tooltip>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              // Index zebra is fine here: this table carries no signal stripes.
              <tr
                key={`${r.product_type}-${i}`}
                className={`border-b border-[var(--line-warm)] hover:bg-[var(--surface-header)] ${
                  i % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'
                }`}
              >
                <td className="px-2 py-1 truncate" title={r.product_type}>{r.product_type}</td>
                <td className="px-2 py-1 text-right tabular-nums">{r.active_units_60d}</td>
                <td className="px-2 py-1 text-right tabular-nums">{r.units_ly}</td>
                <td className="px-2 py-1 text-right tabular-nums font-semibold">{r.forward_window}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </TooltipProvider>
  )
}
